import React from "react";
import classNames from "classnames";
import Helper from "./Styles";

const Input = ({
  textArea = false,
  value = "",
  onChange,
  onBlur,
  placeholder = "",
  type = "text",
  error,
  disabled,
  prefix,
  maxLength,
  showCharCount = false,
  loading = false,
  className="",
  ...rest
}) => {
  const { inputDefaultClass, inputSizeClass, inputFocusClass } = Helper();

  const defaultClass = inputDefaultClass || "border rounded px-3 py-2 w-full";
  const sizeClass = inputSizeClass || "";
  const focusClass =
    inputFocusClass || "focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const errorClass =
    "invalid !border-red-500 !ring-1 !ring-red-500 !ring-opacity-100 focus:ring-red-500 focus:border-red-500";
  const disabledClass =
  "!cursor-not-allowed bg-gray-200 dark:bg-[#373737]";

  const sharedClasses = classNames(
    defaultClass,
    sizeClass,
    focusClass,
    error && errorClass,
    disabled && disabledClass,
    "text-black dark:text-white",
    prefix && "pl-10",
    loading && "pr-10",
    className
  );

  const handleChange = (e) => {
    let inputValue = e.target.value;

    // Remove leading spaces
    inputValue = inputValue.replace(/^\s+/, "");

    if (type === "text") {
      // Capitalize first word
      inputValue = inputValue.replace(/^([^\s]+)(.*)/, (_, firstWord, rest) => {
        const capitalized =
          firstWord.charAt(0).toUpperCase() + firstWord.slice(1);
        return capitalized + rest;
      });

      if (maxLength) {
        inputValue = inputValue.slice(0, maxLength);
      }
    }

    if (type === "number") {
      // Sanitize input: allow digits and only one decimal point
      let sanitized = inputValue.replace(/[^0-9.]/g, "");
      const parts = sanitized.split(".");

      // Keep only the first decimal point, join rest without dots
      if (parts.length > 2) {
        sanitized = parts[0] + "." + parts.slice(1).join("");
      }

      const integerPart = parts[0];
      let decimalPart = parts[1] || "";

      // Limit decimal to 2 digits
      if (decimalPart.length > 2) {
        decimalPart = decimalPart.slice(0, 2);
      }

      inputValue = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;

      // Enforce maxLength if set
      if (maxLength && inputValue.length > maxLength) {
        inputValue = inputValue.slice(0, maxLength);
      }
    }

    onChange?.({ ...e, target: { ...e.target, value: inputValue } });
  };

  const handleBlur = (e) => {
    let val = e.target.value;
    if (val && type === "number") {
      // Parse float and fix to two decimals, fallback to original val if NaN
      const floatVal = parseFloat(val);
      val = isNaN(floatVal) ? val : floatVal.toFixed(2);
    }
    onBlur?.({ ...e, target: { ...e.target, value: val } });
  };

  return (
    <div className="w-full relative">
      {prefix && !textArea && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          {prefix}
        </span>
      )}

      {loading && !textArea && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <svg
            className="animate-spin h-4 w-4 text-gray-400"
            xmlns="https://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.372 0 0 5.372 0 12h4z"
            ></path>
          </svg>
        </div>
      )}

      {textArea ? (
        <>
          <textarea
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            className={`${sharedClasses} ${rest.customHeight || "h-auto"}`}
            rows="4"
            {...rest}
          />
          {showCharCount && maxLength && (
            <div className="text-right text-xs text-gray-400 mt-1">
              {value.length} / {maxLength}
            </div>
          )}
        </>
      ) : (
        <input
          type={type}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={sharedClasses}
          onWheel={(e) => type === "number" && e.currentTarget.blur()}
          {...rest}
        />
      )}
    </div>
  );
};

export default Input;
